// Server reservations protect every station; the existing CIBD/PDF renderer stays shared.
export const createAmendmentPrinter = ({ request, getPoints, prepareCatalog, print, isCurrent, confirmUnknown }) => {
  const assertCurrent = () => {
    if (!isCurrent()) throw new Error('Sessão alterada antes da impressão.');
  };
  return async ({ source, orderId, revision, order, manual = false, printPointIds }) => {
    assertCurrent();
    const points = getPoints(order || { source, orderId });
    const requested = printPointIds || points.map((point) => point.id);
    if (!requested.length) return { ok: false, error: 'Configure uma impressora em um ponto de impressão ativo. A comanda alterada permanece pendente.' };
    // A failed iFood catalog must never acquire a reservation or call CIBD.
    const catalog = await prepareCatalog(source);
    assertCurrent();
    const path = `/api/orders/${encodeURIComponent(orderId)}/amendments/${Number(revision)}`;
    const claimBody = { source, pointIds: requested, manual };
    let claim;
    try {
      claim = await request(`${path}/print-claim`, 'POST', claimBody);
    } catch (error) {
      if (!manual || error.code !== 'AMENDMENT_PRINT_UNKNOWN' || !confirmUnknown()) throw error;
      assertCurrent();
      claim = await request(`${path}/print-claim`, 'POST', { ...claimBody, allowUnknown: true });
    }
    assertCurrent();
    if (!claim.claimed) return ['busy', 'unknown', 'blocked', 'review-required'].includes(claim.reason)
      ? { ok: false, pending: true, error: 'Outra revisão desta comanda está em envio ou aguarda conferência. A nova comanda permanece pendente.' }
      : { ok: true, skipped: true, superseded: claim.reason === 'superseded' };
    if (!claim.claimToken || String(claim.order?.orderId || claim.order?.id) !== String(orderId)
      || Number(claim.order?.amendmentRevision) !== Number(revision)
      || !Array.isArray(claim.pointIds) || !claim.pointIds.length
      || claim.pointIds.some((id) => !requested.includes(id))) throw new Error('Reserva de impressão inválida.');
    const body = { source, claimToken: claim.claimToken };
    const acknowledged = new Set();
    const started = new Set();
    const result = await print(claim.order, {
      catalog, catalogVerified: Boolean(catalog), printPointIds: claim.pointIds,
      canContinue: isCurrent,
      onPointDispatchStart: async (point) => {
        assertCurrent();
        const ack = await request(`${path}/print-start`, 'PATCH', { ...body, pointId: point.id });
        if (ack.accepted !== true) throw new Error('O envio da comanda não foi autorizado.');
        started.add(point.id);
        assertCurrent();
      },
      onPointResult: async (point) => {
        assertCurrent();
        const status = point.ok ? 'sent' : point.dispatchStarted ? 'unknown' : 'failed';
        const ack = await request(`${path}/print-result`, 'PATCH', { ...body, pointResults: [{ pointId: point.pointId, status }] });
        if (ack.accepted !== true) throw new Error('Resultado de impressão não confirmado.');
        acknowledged.add(point.pointId);
      },
    });
    // Preflight/PDF/start failures never reach onPointResult. Release only points whose physical send did not start.
    const unstarted = claim.pointIds.filter((id) => !acknowledged.has(id) && !started.has(id));
    if (unstarted.length && isCurrent()) {
      await request(`${path}/print-result`, 'PATCH', { ...body,
        pointResults: unstarted.map((pointId) => ({ pointId, status: 'failed' })) });
    }
    const uncertain = claim.pointIds.some((id) => started.has(id) && !acknowledged.has(id));
    return uncertain ? { ...result, ok: false, unknown: true,
      error: 'A comanda pode ter sido impressa. Confira o papel antes de reimprimir.' } : result;
  };
};
